import React, { useRef, useEffect, useState } from 'react';
import './editable-textarea.scss';

const EditableTextarea = ({ name = '', value = '', onChange = () => { }, tags = [], textLength = 5000 }) => {
  const ref = useRef(null);
  const savedSelection = useRef({ start: 0, end: 0 });

  const [inputValue, setInputValue] = useState(value);

  const saveCaretPosition = () => {
    if (!ref.current) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(ref.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    savedSelection.current.start = preCaretRange.toString().length;
    preCaretRange.setStart(range.startContainer, range.startOffset);
    savedSelection.current.end = savedSelection.current.start + preCaretRange.toString().length;
  };

  const restoreCaretPosition = () => {
    if (!ref.current) return;

    const { start } = savedSelection.current;
    let charIndex = 0;
    let stop = { found: false, node: null, offset: 0 };

    const walker = document.createTreeWalker(
      ref.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeLength = node.nodeValue.length;

      if (!stop.found) {
        if (charIndex + nodeLength >= start) {
          stop.found = true;
          stop.node = node;
          stop.offset = start - charIndex;
        }
        charIndex += nodeLength;
      }
    }

    if (stop.node) {
      const range = document.createRange();
      range.setStart(stop.node, stop.offset);
      range.collapse(true);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Если не нашли узел (например, после Enter в пустой строке),
      // перемещаем каретку в конец
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      range.collapse(false); // в конец

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const updateContentWithTags = (text) => {
    if (!ref.current) return;

    saveCaretPosition();
    ref.current.innerHTML = '';
    const parts = text.split(/(\{\{[^}]+\}\})/g);

    parts.forEach((part) => {
      if (part.match(/^\{\{[^}]+\}\}$/)) {
        const span = document.createElement('span');
        span.className = 'highlighted-tag';
        span.textContent = part;
        ref.current.appendChild(span);
      } else {
        ref.current.appendChild(document.createTextNode(part));
      }
    });

    restoreCaretPosition();
  };

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }

    updateContentWithTags(value);
    // eslint-disable-next-line
  }, [value]);

  const handleInsertTag = (tag) => {
    if (!ref.current) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const span = document.createElement('span');
    span.className = 'highlighted-tag';
    span.textContent = tag;

    range.insertNode(span);

    const newRange = document.createRange();
    newRange.setStartAfter(span);
    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    updateValueFromDOM();
  };

  const handleInput = () => {
    updateValueFromDOM();
  };

  const updateValueFromDOM = () => {
    if (!ref.current) return;

    // Получаем текст (без HTML)
    let text = ref.current.innerText;

    if (text.length > textLength) {
      text = text.substring(0, textLength);
      onChange(text);
      updateContentWithTags(text);
      return;
    }

    onChange(text);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text/plain');
    const currentText = ref.current.innerText;

    if (currentText.length + paste.length > textLength) {
      const remaining = textLength - currentText.length;
      if (remaining > 0) {
        document.execCommand('insertText', false, paste.substring(0, remaining));
      }
    } else {
      document.execCommand('insertText', false, paste);
    }

    updateValueFromDOM();
  };

  useEffect(() => {
    if (ref.current && inputValue !== ref.current.innerText) {
      updateContentWithTags(inputValue);
    }
    // eslint-disable-next-line
  }, [inputValue]);

  return (
    <div className="editable-textarea">
      <label className="et-label">{name}</label>
      <div className="tag-buttons">
        {tags.map((tag, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleInsertTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      <div
        className="edit-textarea__container"
        ref={ref}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning={true}
      />

      <div className="symbols-total">
        <h6>Symbols: {inputValue.length} / {textLength}</h6>
      </div>
    </div>
  );
};

export default EditableTextarea;